import {
  Box,
  Typography,
  Stack,
  MenuItem,
  Select,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import BookingsAtom from "./BookingsAtom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Helper per ottenere mese e anno da una data
const formatMonthYear = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    year: "numeric",
  };
  return date.toLocaleDateString("it-IT", options).toUpperCase(); // Formatta come "SETTEMBRE 2024"
};

// Funzione per raggruppare le prenotazioni per mese
const groupByMonth = (documents: any[]) => {
  const grouped = documents.reduce((acc: Record<string, any[]>, doc) => {
    const startDate = new Date(doc.startDate.seconds * 1000); // Converti timestamp in data
    const monthYear = startDate.toISOString().slice(0, 7); // Restituisce "YYYY-MM"

    if (!acc[monthYear]) {
      acc[monthYear] = []; // Se non esiste già il gruppo per questo mese, lo crea
    }

    acc[monthYear].push(doc); // Aggiungi il documento al gruppo del mese corrispondente
    return acc;
  }, {});

  // Ordina l'oggetto `grouped` per chiave (che rappresenta il mese e anno)
  return Object.fromEntries(
    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
  );
};

type Props = {
  availabilityDocuments: any[];
  isPrice?: boolean;
  isList?: boolean;
};

const Bookings: React.FC<Props> = ({
  isList,
  availabilityDocuments,
  isPrice,
}) => {
  const filteredDocuments = availabilityDocuments.filter(
    (doc) => doc.isManual || doc.isWebsite
  );

  const groupedDocuments = groupByMonth(filteredDocuments); // Raggruppa per mese
  const availableMonths = Object.keys(groupedDocuments); // Ottieni tutti i mesi disponibili

  const currentMonthYear = new Date().toISOString().slice(0, 7); // Mese corrente in formato "YYYY-MM"
  const initialMonth = availableMonths.includes(currentMonthYear)
    ? currentMonthYear
    : availableMonths[0]; // Usa il mese corrente se disponibile, altrimenti il primo mese disponibile
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value); // Cambia il mese selezionato
  };
  const handlePreviousMonth = () => {
    const currentIndex = availableMonths.indexOf(selectedMonth);
    if (currentIndex > 0) {
      setSelectedMonth(availableMonths[currentIndex - 1]);
    }
  };
  const handleNextMonth = () => {
    const currentIndex = availableMonths.indexOf(selectedMonth);
    if (currentIndex < availableMonths.length - 1) {
      setSelectedMonth(availableMonths[currentIndex + 1]);
    }
  };

  return (
    <Stack
      sx={{
        height: isList ? "100vh" : undefined,
        backgroundColor: isList ? "black" : undefined,
      }}
      justifyContent={"start"}
      width={"100%"}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <Select
          value={selectedMonth}
          onChange={handleMonthChange}
          displayEmpty
          inputProps={{ "aria-label": "Select Month" }}
          sx={{
            backgroundColor: "white",
            width: "100vw",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {availableMonths.map((monthYear) => {
            const date = new Date(`${monthYear}-01`);
            const formattedMonthYear = formatMonthYear(date);

            return (
              <MenuItem
                sx={{
                  backgroundColor: "white",
                }}
                key={monthYear}
                value={monthYear}
              >
                <Typography textAlign={"center"}>
                  {formattedMonthYear}
                </Typography>
              </MenuItem>
            );
          })}
        </Select>

        {/* Freccia avanti */}
      </Box>
      {/* @ts-ignore */}

      <Typography
        sx={{
          pt: 4,
        }}
        variant={"h5"}
        textAlign={"center"}
      >
        Prenotazioni{" "}
      </Typography>

      <Box>
        {selectedMonth && groupedDocuments[selectedMonth] && (
          <Box m={"20px 0"}>
            <Typography
              variant="h6"
              color={"black"}
              textAlign={"center"}
              sx={{
                color: isList ? "white" : "black",
                fontSize: "26px",
                letterSpacing: 1.3,
                fontFamily: "degular",
              }}
            >
              {" "}
              <IconButton
                sx={{ color: "white" }}
                onClick={handlePreviousMonth}
                disabled={availableMonths.indexOf(selectedMonth) === 0} // Disabilitata se è il primo mese
              >
                <ArrowBackIcon />
              </IconButton>
              {formatMonthYear(new Date(`${selectedMonth}-01`))}{" "}
              <IconButton
                sx={{ color: "white" }}
                onClick={handleNextMonth}
                disabled={
                  availableMonths.indexOf(selectedMonth) ===
                  availableMonths.length - 1
                } // Disabilitata se è l'ultimo mese
              >
                <ArrowForwardIcon />
              </IconButton>
            </Typography>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                width: "100%",
                height: "70vh",
                justifyContent: "start",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {["room1", "room2", "room3", "room4", "room5"].map((roomId) => {
                const roomDocuments = groupedDocuments[selectedMonth].filter(
                  (doc) => doc.id.includes(roomId)
                );
                return roomDocuments.length > 0 ? (
                  <BookingsAtom
                    key={roomId}
                    roomDocuments={roomDocuments}
                    room={roomId.replace("room", "")} // Ottieni il numero della camera
                    isPrice={isPrice}
                    isList={isList}
                  />
                ) : null; // Non renderizzare nulla se non ci sono documenti
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default Bookings;
